import { GET_VEHICLETREE } from "@App/graphql/queries";
import type { Brand, Organization } from "@App/types";
import { useQuery } from "@apollo/client";
import { useMemo, useState } from "react";

interface useBrandFilterProps {
  organization: Organization;
}

export function useBrandFilter({ organization }: useBrandFilterProps) {
  const { data, loading } = useQuery<{ brands?: Brand[] }>(GET_VEHICLETREE, {
    variables: {
      ...(organization && { organization: +organization.id }),
      includeGenerations: false,
      includeMotorizations: false,
    },
  });
  const [query, setQuery] = useState<string>("");

  // sort brands by popularity and alphabetically `[...popularBrands, ...regularBrands]`
  function sortBrands(items: Brand[]): Brand[] {
    return [...items].sort((a, b) => {
      const aIsImportant = !!(a.important && a.logo);
      const bIsImportant = !!(b.important && b.logo);
      if (aIsImportant !== bIsImportant) return aIsImportant ? -1 : 1;
      return a.name.localeCompare(b.name, undefined, {
        sensitivity: "base",
      });
    });
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: including sortBrand would cause re-computation on every re-render
  const filteredBrands = useMemo(() => {
    if (!data?.brands) return [];
    const sorted = sortBrands(data.brands);
    if (!query) return sorted;
    return sorted.filter((b) => b.name.toLowerCase().includes(query.toLowerCase()));
  }, [data?.brands, query]);

  const popularBrands = useMemo(() => {
    return filteredBrands.filter((b) => b.important && b.logo);
  }, [filteredBrands]);

  const regularBrands = useMemo(() => {
    return filteredBrands.filter((b) => !b.important || !b.logo);
  }, [filteredBrands]);

  const showDivider = !!popularBrands.length && !!regularBrands.length;

  return {
    popularBrands,
    regularBrands,
    showDivider,
    loading,
    setQuery,
  };
}
